import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { solicitarEliminacion, cancelarEliminacion, eliminarMiUsuario, fechaEliminacion, DIAS_PARA_ELIMINAR } from '../services/cuentaService';

const fecha = (f) => new Date(f).toLocaleDateString('es-HN', { day: 'numeric', month: 'long', year: 'numeric' });
const ROLES = { duena: 'Dueña', vendedora: 'Vendedor/a', admin: 'Administrador/a' };

// "Mi cuenta": datos de acceso y "Eliminar mi cuenta" (exigido por Google Play
// y App Store). La dueña elimina TODO su negocio con 30 días para arrepentirse;
// alguien del equipo elimina solo su usuario, al momento.
export default function MiCuentaView({ onVolver }) {
  const { sesion, negocio, suscripcion, recargarSuscripcion, cerrarSesion } = useAuth();
  const { mostrarToast } = useToast();
  const esDuena = negocio?.rol === 'duena';
  const pendiente = suscripcion?.eliminacion_solicitada_en;
  const [abierto, setAbierto] = useState(false);
  const [confirmacion, setConfirmacion] = useState('');
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const confirmar = async () => {
    setOcupado(true);
    try {
      if (esDuena) {
        await solicitarEliminacion(motivo.trim());
        await recargarSuscripcion();
        setAbierto(false);
        mostrarToast('Solicitud recibida. Tienes 30 días para cancelarla si cambias de opinión.', 'exito', 7000);
      } else {
        await eliminarMiUsuario();
        mostrarToast('Tu cuenta fue eliminada. ¡Gracias por usar Kontrol!', 'exito', 6000);
        setTimeout(() => cerrarSesion(), 1500);
      }
    } catch (e) { mostrarToast(e.message, 'error', 7000); }
    setOcupado(false);
  };

  const cancelar = async () => {
    setOcupado(true);
    try {
      await cancelarEliminacion();
      await recargarSuscripcion();
      mostrarToast('Listo, cancelaste la eliminación. Tu cuenta sigue igual. 💜', 'exito', 6000);
    } catch (e) { mostrarToast(e.message, 'error'); }
    setOcupado(false);
  };

  const palabra = 'ELIMINAR';
  const fila = { display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '10px 0', borderTop: '1px solid #F3F0FA', flexWrap: 'wrap', fontSize: '14px' };

  return (
    <div className="k-page">
      <div className="k-page-head">
        <h2 className="k-page-title">👤 Mi cuenta</h2>
        <button onClick={onVolver} className="k-btn-soft">← Volver</button>
      </div>

      <div className="k-card" style={{ padding: '6px 16px', marginBottom: '16px' }}>
        <div style={{ ...fila, borderTop: 'none' }}><span style={{ color: '#6B7280' }}>Correo</span><b style={{ wordBreak: 'break-all' }}>{sesion?.user?.email}</b></div>
        <div style={fila}><span style={{ color: '#6B7280' }}>Negocio</span><b>{negocio?.nombre}</b></div>
        <div style={fila}><span style={{ color: '#6B7280' }}>Tu rol</span><b>{ROLES[negocio?.rol] || negocio?.rol}</b></div>
      </div>

      {pendiente ? (
        <div className="k-card" style={{ padding: '16px', background: '#FEF2F2', border: '1px solid #FCA5A5' }}>
          <b style={{ color: '#991B1B' }}>Tu cuenta se eliminará el {fecha(fechaEliminacion(pendiente))}</b>
          <p style={{ margin: '6px 0 12px', fontSize: '14px', color: '#374151' }}>
            Mientras tanto tu cuenta está en pausa: puedes ver tu información y descargarla, pero no registrar cambios.
            Si cambias de opinión, cancela antes de esa fecha y todo sigue igual.
          </p>
          <button onClick={cancelar} disabled={ocupado}
            style={{ padding: '11px 16px', border: 'none', borderRadius: '10px', background: 'var(--color-primario,#6D28D9)', color: '#fff', fontWeight: 700, fontFamily: 'inherit', cursor: ocupado ? 'wait' : 'pointer' }}>
            {ocupado ? 'Un momento…' : 'Cancelar la eliminación'}
          </button>
        </div>
      ) : !abierto ? (
        <div className="k-card" style={{ padding: '16px' }}>
          <b>Eliminar mi cuenta</b>
          <p style={{ margin: '6px 0 12px', fontSize: '14px', color: '#374151' }}>
            {esDuena
              ? `Se eliminan tu negocio y toda su información: productos, fotos, ventas, clientes, servicios y tu equipo. Tienes ${DIAS_PARA_ELIMINAR} días para arrepentirte.`
              : 'Se elimina tu usuario y ya no podrás entrar a este negocio. Las ventas que registraste se quedan en el negocio.'}
          </p>
          <button onClick={() => setAbierto(true)}
            style={{ padding: '10px 14px', border: '1px solid #FCA5A5', borderRadius: '10px', background: '#fff', color: '#B91C1C', fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}>
            Eliminar mi cuenta
          </button>
        </div>
      ) : (
        <div className="k-card" style={{ padding: '16px', border: '1px solid #FCA5A5' }}>
          <b style={{ fontSize: '16px', color: '#991B1B' }}>¿Seguro que quieres eliminar tu cuenta?</b>
          <ul style={{ margin: '8px 0 12px', paddingLeft: '18px', fontSize: '14px', color: '#374151', display: 'grid', gap: '4px' }}>
            {esDuena ? (
              <>
                <li>Tu cuenta queda en pausa desde hoy y <b>se elimina definitivamente el {fecha(fechaEliminacion(new Date()))}</b>.</li>
                <li>Se borran tu negocio, productos, fotos, ventas, clientes, servicios, cotizaciones y las cuentas de tu equipo.</li>
                <li>Si tienes un plan pagado, no se renueva.</li>
                <li>Antes de esa fecha puedes cancelar y todo sigue igual. Te recomendamos descargar tus reportes antes.</li>
              </>
            ) : (
              <>
                <li>Se elimina tu usuario <b>ahora mismo</b> y no se puede deshacer.</li>
                <li>Las ventas que registraste se quedan en el negocio.</li>
              </>
            )}
          </ul>
          {esDuena && (
            <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} maxLength={250} disabled={ocupado}
              placeholder="¿Por qué te vas? (opcional, nos ayuda a mejorar)" aria-label="Motivo (opcional)"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '10px', fontSize: '14px', fontFamily: 'inherit', marginBottom: '10px' }} />
          )}
          <label htmlFor="confirmar-eliminar" style={{ display: 'block', fontSize: '13px', fontWeight: 700, marginBottom: '4px' }}>
            Para confirmar, escribe <span style={{ fontFamily: 'monospace', color: '#B91C1C' }}>{palabra}</span>
          </label>
          <input id="confirmar-eliminar" value={confirmacion} onChange={(e) => setConfirmacion(e.target.value)} disabled={ocupado} autoComplete="off"
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '10px', fontSize: '14px', fontFamily: 'inherit', marginBottom: '12px' }} />
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button onClick={() => { setAbierto(false); setConfirmacion(''); }} disabled={ocupado}
              style={{ padding: '10px 14px', border: 'none', borderRadius: '10px', background: 'var(--color-primario,#6D28D9)', color: '#fff', fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' }}>
              No, mantener mi cuenta
            </button>
            <button onClick={confirmar} disabled={ocupado || confirmacion.trim().toUpperCase() !== palabra}
              style={{ padding: '10px 14px', border: '1px solid #FCA5A5', borderRadius: '10px', background: '#fff', fontWeight: 700, fontFamily: 'inherit',
                color: confirmacion.trim().toUpperCase() === palabra ? '#B91C1C' : '#9CA3AF', cursor: ocupado ? 'wait' : 'pointer' }}>
              {ocupado ? 'Eliminando…' : 'Sí, eliminar mi cuenta'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
